<template>
  <UFormField label="Categorie">
    <div
      ref="listRef"
      class="category-filter-list space-y-2 overflow-y-auto pr-1"
      :class="{ 'category-filter-list--scrollable': categories.length > VISIBLE_ROWS }"
    >
      <NuxtLink
        v-for="cat in categories"
        :key="cat.id"
        :to="isSelected(cat) ? shopPath('/produse') : shopPath(`/categorii/${cat.slug}`)"
        class="flex items-center gap-3 rounded-lg border p-2 transition-colors"
        :class="isSelected(cat)
          ? 'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-950/30'
          : 'border-default hover:border-muted hover:bg-muted'"
        :style="cat.depth ? { marginLeft: `${cat.depth * 0.75}rem` } : undefined"
        :data-selected="isSelected(cat) ? '' : undefined"
        @mouseenter="prefetchCategory(cat)"
        @focus="prefetchCategory(cat)"
      >
        <div class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
          <AppImage
            v-if="cat.image"
            :src="cat.image"
            :alt="cat.name"
            preset="thumbnail"
            width="40"
            height="40"
            sizes="40px"
            loading="lazy"
            class="h-full w-full object-cover"
          />
          <UIcon v-else name="i-heroicons-squares-2x2" class="h-5 w-5 text-dimmed" />
        </div>
        <span
          class="flex-1 truncate text-sm font-medium"
          :class="isSelected(cat) ? 'text-primary-700 dark:text-primary-300' : 'text-highlighted'"
        >
          {{ cat.name }}
        </span>
        <span v-if="cat.productCount != null" class="shrink-0 text-xs text-muted">
          ({{ cat.productCount }})
        </span>
      </NuxtLink>

      <p v-if="!categories.length" class="text-sm text-muted">
        Nu există categorii.
      </p>
    </div>
  </UFormField>
</template>

<script setup lang="ts">
import { flattenCategoryTree, type CategoryRow } from '~/utils/categories'

const route = useRoute()
const shopPath = useShopPath()
const productsStore = useProductsStore()
const { prefetchRoute } = useRoutePrefetch()

const VISIBLE_ROWS = 5
const listRef = ref<HTMLElement | null>(null)

const categories = computed(() => flattenCategoryTree(productsStore.categories))

const currentSlug = computed(() => {
  if (!route.path.includes('/categorii/')) return ''
  return String(route.params.slug || '')
})

function isSelected(category: CategoryRow) {
  return currentSlug.value === category.slug
}

function prefetchCategory(category: CategoryRow) {
  if (!isSelected(category)) {
    prefetchRoute(shopPath(`/categorii/${category.slug}`))
  } else {
    prefetchRoute(shopPath('/produse'))
  }
}

function scrollSelectedIntoView() {
  nextTick(() => {
    listRef.value?.querySelector('[data-selected]')?.scrollIntoView({ block: 'nearest' })
  })
}

watch(categories, scrollSelectedIntoView)
watch(currentSlug, scrollSelectedIntoView)

onMounted(() => {
  if (!productsStore.categories.length) {
    productsStore.fetchCategories()
  }
  scrollSelectedIntoView()
})
</script>

<style scoped>
/* 5 rows: item (p-2 + h-10) + gaps (space-y-2) */
.category-filter-list--scrollable {
  max-height: calc(5 * 3.5rem + 4 * 0.5rem);
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--ui-border) 80%, transparent) transparent;
}

.category-filter-list--scrollable::-webkit-scrollbar {
  width: 6px;
}

.category-filter-list--scrollable::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background-color: color-mix(in srgb, var(--ui-border) 80%, transparent);
}
</style>
